import {
  type FC,
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
} from 'react';
import { Flex } from 'antd';
import styled from 'styled-components';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import type { SourceDimensionsMap } from '../joins/useAlgorithmJoinsDataQuery';
import { getAlgorithmSourceShortLabel } from '../utils';
import translations from './AlgorithmMappingsFormModal.translations.json';
import {
  confirmPanelFormClose,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  Form,
  FormAlerts,
  FormField,
  FormInput,
  FormRef,
  FormSelect,
  getFormTableFieldName,
  TanstackFormTable,
  useAlertContext,
} from '../../../common/form';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '../../../configuration';
import { useFormState } from 'react-hook-form';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  colorGray,
  colorTableOperationIcon,
} from '@atlas-frontend/design-tokens';
import { Padding } from '../../../common/components/Padding';
import * as Icon from 'react-feather';
import { AlgorithmMappingClassifierMappingFormSelect } from './AlgorithmMappingClassifierMappingFormSelect';
import { AlgorithmMappingsFormStore } from './AlgorithmMappingsFormStore';
import { AlgorithmMappingSourceDimensionSelect } from './AlgorithmMappingSourceDimensionSelect';
import { confirmationHeaders, getMapBy } from '../../../common/utils';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutationErrors } from '../../../common/queryClient';
import validationTranslations from '../../translations/validation.translations.json';
import { DbSourceDimensionFormField } from './DbSourceDimensionFormField';
import { useMutateAlgorithmMappingsQuery } from './query/useMutateAlgorithmMappingsQuery';
import { ActionButton } from '../../../common/components';
import { AlgorithmMappingFormDataSchema } from './schema/AlgorithmMappingFormDataSchema';
import { AlgorithmMappingsValidFormDataSchema } from './schema/AlgorithmMappingsValidFormDataSchema';
import { defaultMappingFormData } from './schema/defaultValues';
import type {
  AlgorithmMappingQueryDtoType,
  AlgorithmMappingsValidFormDataType,
  AlgorithmMappingValidFormDataType,
} from './schema/types';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';

const { addTranslations, t } = i18nNsFactory('algorithm-mappings-modal');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface AlgorithmMappingsFormModalProps {
  algorithmId: number;
  sources: AlgorithmSourceFormDataType[];
  sourceDimensionsMap: SourceDimensionsMap;
  mappings: AlgorithmMappingQueryDtoType[];
  onClose?: () => void;
  onChange?: () => void;
  actions?: ReactNode;
  transition?: boolean;
  formRef: RefObject<FormRef<AlgorithmMappingsValidFormDataType>>;
}

export const AlgorithmMappingsFormModal: FC<AlgorithmMappingsFormModalProps> = (
  props,
) => {
  const {
    algorithmId,
    onClose,
    onChange,
    mappings,
    sources,
    sourceDimensionsMap,
    formRef,
    ...rest
  } = props;

  const { mutateAsync } = useMutateAlgorithmMappingsQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const handleSubmit = useCallback(
    (values: AlgorithmMappingsValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: algorithmId,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(() => {
          onChange?.();
          onClose?.();
          setSuccess();
          formRef.current.supressBlock();
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      algorithmId,
      onChange,
      onClose,
      setSuccess,
      setMutationErrors,
      formRef,
    ],
  );

  const isDbSources = useMemo(
    () => sources.some((source) => source.sourceType === 'DB'),
    [sources],
  );

  const defaultValues = useMemo(
    () => ({
      mappings: mappings.length
        ? mappings.map((algorithmMapping) =>
            AlgorithmMappingFormDataSchema.parse({
              mapping: algorithmMapping,
              sourceDimensionsMap,
            }),
          )
        : [defaultMappingFormData],
    }),
    [mappings, sourceDimensionsMap],
  );

  const algorithmSourcesBySourceType = useMemo(() => {
    const map = new Map<'DB' | 'DOD', AlgorithmSourceFormDataType[]>([
      ['DB' as const, []],
      ['DOD' as const, []],
    ]);

    if (sources) {
      for (const source of sources) {
        map.get(source.sourceType === 'DB' ? 'DB' : 'DOD')!.push(source);
      }
    }

    return map;
  }, [sources]);

  const algorithmSourcesById: Map<number, AlgorithmSourceFormDataType> =
    useMemo(() => getMapBy(sources || [], 'id'), [sources]);

  return (
    <Form
      formRef={formRef}
      schema={AlgorithmMappingsValidFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="algorithmMappings"
    >
      <AlgorithmMappingsFormStore.Provider
        initialData={{
          algorithmSourcesBySourceType,
          algorithmSourcesById,
          isDbSources,
        }}
      >
        <AlgorithmMappingFormModalInner onClose={onClose} {...rest} />
      </AlgorithmMappingsFormStore.Provider>
    </Form>
  );
};

const columnHelper = createColumnHelper<AlgorithmMappingValidFormDataType>();

function getColumns(
  isDbSources: boolean,
  algorithmSourcesByReference: Map<string, AlgorithmSourceFormDataType>,
  mappingJoinTypes?: SystemClassifierOption[],
): ColumnDef<AlgorithmMappingValidFormDataType, any>[] {
  const columns: ColumnDef<AlgorithmMappingValidFormDataType, any>[] = [];
  if (isDbSources) {
    columns.push(
      columnHelper.accessor('sourceDimension', {
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => (
          <DbSourceDimensionCell
            {...props}
            algorithmSourcesByReference={algorithmSourcesByReference}
          />
        ),
        meta: {
          width: 120,
          className: 'hide-box-shadow',
        },
      }),
    );
  } else {
    columns.push(
      columnHelper.accessor('sourceDimension', {
        enableColumnFilter: false,
        enableSorting: false,
        cell: DataObjectDescriptionDimensionCell,
        meta: {
          width: 120,
          className: 'hide-box-shadow',
        },
      }),
    );
  }
  columns.push(
    columnHelper.accessor('classifierMapping', {
      enableColumnFilter: false,
      enableSorting: false,
      cell: (props) => (
        <ClassifierMappingSelect {...props} areDbSources={isDbSources} />
      ),
      meta: {
        width: 80,
        className: 'hide-box-shadow',
      },
    }),
    columnHelper.accessor('joinTypeCode', {
      enableColumnFilter: false,
      enableSorting: false,
      cell: (props) => (
        <JoinTypeSelect
          {...props}
          areDbSources={isDbSources}
          mappingJoinTypes={mappingJoinTypes}
        />
      ),
      meta: {
        width: 50,
        className: 'hide-box-shadow',
      },
    }),
    columnHelper.accessor('alias', {
      enableColumnFilter: false,
      enableSorting: false,
      cell: (props) => <AliasCell {...props} areDbSources={isDbSources} />,
      meta: {
        width: 60,
        className: 'hide-box-shadow',
      },
    }),
    columnHelper.accessor('operations' as any, {
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => {
        const rows = props.table.getCenterRows().length;
        return (
          <FlexWrapper vertical justify="end">
            <Flex justify="left" gap="small">
              {props.row.index + 1 === rows && (
                <CreateFormTableRowButtonWrapper
                  defaultRowValues={defaultMappingFormData}
                  icon={<Icon.Plus size={12} color={colorTableOperationIcon} />}
                  autoFocusField="sourceDimension"
                />
              )}
              <DeleteFormTableRowButtonWrapper
                context={props}
                icon={<Icon.Minus size={12} color={colorTableOperationIcon} />}
              />
            </Flex>
          </FlexWrapper>
        );
      },
      meta: {
        width: 50,
        className: 'sticky-right hide-box-shadow',
      },
    }),
  );

  return columns;
}

interface AlgorithmMappingFormModalInnerProps {
  onClose?: () => void;
  actions?: ReactNode;
  transition?: boolean;
}

const AlgorithmMappingFormModalInner: FC<
  AlgorithmMappingFormModalInnerProps
> = (props) => {
  const { onClose, transition, actions } = props;
  const { cols } = useMappingTable();
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      transitionName={transition ? undefined : ''}
      maskTransitionName={transition ? undefined : ''}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="70rem"
    >
      <ModalHeader>
        <ModalTitle>{actions}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <StyledFormTable name="mappings" columns={cols} hideHeader={true} />
      </Padding>
    </Modal>
  );
};

function useMappingTable() {
  const { MAPPING_JOIN_TYPE: mappingJoinTypes } =
    useSystemClassifierSelectOptions(['MAPPING_JOIN_TYPE'], 'code');
  const [isDbSources] = AlgorithmMappingsFormStore.useValue('isDbSources');
  const [algorithmSourcesById] = AlgorithmMappingsFormStore.useValue(
    'algorithmSourcesById',
  );

  const algorithmSourcesByReference = useMemo(
    () =>
      getMapBy(Array.from(algorithmSourcesById.values()), (algorithmSource) =>
        getAlgorithmSourceShortLabel(algorithmSource).toLowerCase(),
      ),
    [algorithmSourcesById],
  );

  const cols = useMemo(
    () =>
      getColumns(isDbSources, algorithmSourcesByReference, mappingJoinTypes),
    [isDbSources, mappingJoinTypes, algorithmSourcesByReference],
  );

  return { cols };
}

const DbSourceDimensionCell: FC<
  CellContext<AlgorithmMappingValidFormDataType, any> & {
    algorithmSourcesByReference: Map<string, AlgorithmSourceFormDataType>;
  }
> = memo((props) => {
  const name = getFormTableFieldName(
    props,
  ) as `mappings.${number}.sourceDimension`;

  const [algorithmSourcesById] = AlgorithmMappingsFormStore.useValue(
    'algorithmSourcesById',
  );

  return (
    <DbSourceDimensionFormField<AlgorithmMappingsValidFormDataType>
      name={name}
      label={t('sourceDimension.label')}
      algorithmSourcesById={algorithmSourcesById}
    />
  );
});

const DataObjectDescriptionDimensionCell: FC<
  CellContext<AlgorithmMappingValidFormDataType, any>
> = memo((props) => {
  const name = getFormTableFieldName(props);

  return (
    <FormField name={name} label={t('sourceDimension.label')} required>
      <AlgorithmMappingSourceDimensionSelect name={name} />
    </FormField>
  );
});

const ClassifierMappingSelect: FC<
  CellContext<AlgorithmMappingValidFormDataType, any> & {
    areDbSources: boolean;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);

  return (
    <FormField name={name} label={t('classifierMapping.label')} required>
      <AlgorithmMappingClassifierMappingFormSelect
        name={name}
        areDbSources={props.areDbSources}
        style={props.areDbSources ? undefined : { marginTop: 22 }}
      />
    </FormField>
  );
});

const JoinTypeSelect: FC<
  CellContext<AlgorithmMappingValidFormDataType, any> & {
    areDbSources: boolean;
    mappingJoinTypes?: SystemClassifierOption[];
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField name={name} label={t('joinType.label')}>
      <FormSelect
        name={name}
        options={props.mappingJoinTypes}
        allowClear
        style={props.areDbSources ? undefined : { marginTop: 22 }}
      />
    </FormField>
  );
});

const AliasCell: FC<
  CellContext<AlgorithmMappingValidFormDataType, any> & {
    areDbSources: boolean;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField name={name} label={t('alias.label')} required>
      <FormInput
        name={name}
        style={props.areDbSources ? undefined : { marginTop: 22 }}
      />
    </FormField>
  );
});

const DeleteFormTableRowButtonWrapper = styled(DeleteFormTableRowButton)`
  border-color: ${colorGray[3]};
`;

const CreateFormTableRowButtonWrapper = styled(CreateFormTableRowButton)`
  border-color: ${colorGray[3]};
`;

const FlexWrapper = styled(Flex)`
  height: 100%;
`;

const StyledFormTable = styled(TanstackFormTable<any, any, any>)`
  td {
    padding: 0.7rem 0.5rem;
    border: none;
  }
  tr:nth-child(n + 2) .form-field > label {
    display: none;
  }
`;
